import {
  BlankCanvasDetailsStep,
  emptyBlankCanvasDetails,
} from "../../molecules/BlankCanvasDetailsStep";
import {
  MethodPickerStep,
  RecipePickerStep,
  agentRecipeTitle,
  initialRecipePickerValues,
} from "../../molecules/RecipePickerStep";
import {
  AiStoryDetailsStep,
  emptyAiStoryDetails,
} from "../../molecules/AiStoryDetailsStep";
import {
  AgentStoryDetailsStep,
  emptyAgentStoryDetails,
} from "../../molecules/AgentStoryDetailsStep";
import type {
  AgentStoryDetailsValues,
  AiStoryDetailsValues,
  BlankCanvasDetailsValues,
  DataStoryStepId,
  RecipePickerValues,
} from "../../../services/dataStories/dataStoryFlow.types";
import type {
  DataStoryStepConfig,
  DataStoryStepRegistry,
} from "./dataStoryStepRegistry.types";

/**
 * Identity helper that pins `TValues` at the definition site. Without it each
 * entry would widen to `DataStoryStepConfig<any>` inside the registry literal
 * and `canContinue` / `nextStepId` would stop being checked against the values
 * type their `Body` actually consumes.
 */
const defineDataStoryStep = <TValues,>(
  config: DataStoryStepConfig<TValues>,
): DataStoryStepConfig<TValues> => config;

/** Figma 5326:88009 — pick a creation method or a template. */
const recipeStep = defineDataStoryStep<RecipePickerValues>({
  id: "recipe",
  title: "Create data story",
  subtitle: "Select a method to begin with",
  primaryActionLabel: "Continue",
  Body: RecipePickerStep,
  initialValues: initialRecipePickerValues,
  // The picker always has a selection (it opens on "ai"), so Continue is never
  // blocked here. Stated explicitly rather than omitted so the contract holds.
  canContinue: () => true,
  nextStepId: ({ selectedRecipeId }) =>
    DETAILS_STEP_BY_RECIPE[selectedRecipeId] ?? "agent-details",
});

/**
 * The new Story path's first step (F04): every way to start in one step, with the goal asked here.
 * Start with AI and Blank canvas are live; From a file and the templates are drawn Soon by the body.
 * A Story made with AI is drafted from the goal typed here ("Draft the plan"); a blank canvas has
 * nothing more to ask (it is ready on creation, with no plan to review). Both submit from here.
 */
const methodStep = defineDataStoryStep<RecipePickerValues>({
  id: "method",
  title: "Create a data story",
  subtitle: "Pick how to start. You can change everything later.",
  primaryActionLabel: ({ selectedRecipeId }) =>
    selectedRecipeId === "blank-canvas" ? "Create story" : "Draft the plan",
  Body: MethodPickerStep,
  initialValues: () => ({
    ...initialRecipePickerValues(),
    goal: "",
    audiences: [],
    audienceDraft: "",
    isAddingAudience: false,
  }),
  // A blank canvas needs nothing; an AI story is made from its goal, so that is required.
  canContinue: ({ selectedRecipeId, goal }) =>
    selectedRecipeId === "blank-canvas" || (goal ?? "").trim().length > 0,
  nextStepId: () => null,
});

/**
 * Which details frame each creation *method* leads to. A lookup rather than a
 * chain of ternaries in `nextStepId`.
 *
 * Only the two methods are listed: everything else in the picker is an agent
 * card, and they all share one frame, so they fall through to the default in
 * `nextStepId` — a new agent card needs no entry here.
 */
const DETAILS_STEP_BY_RECIPE: Partial<
  Record<RecipePickerValues["selectedRecipeId"], DataStoryStepId>
> = {
  "blank-canvas": "blank-details",
  ai: "ai-details",
};

/** Figma 5326:88287 — name, goal and audience for a blank story. */
const blankDetailsStep = defineDataStoryStep<BlankCanvasDetailsValues>({
  id: "blank-details",
  title: "Add details",
  subtitle: "Add details to create a data story",
  primaryActionLabel: "Continue",
  Body: BlankCanvasDetailsStep,
  initialValues: emptyBlankCanvasDetails,
  // A story needs a name; goal and audience are optional in the frame (no
  // required markers) and the backend defaults them.
  canContinue: ({ name }) => name.trim().length > 0,
  nextStepId: () => null,
});

/** Figma 5326:88121 — goal, audience and sources for an AI-planned story. */
const aiDetailsStep = defineDataStoryStep<AiStoryDetailsValues>({
  id: "ai-details",
  title: "Add details",
  subtitle: "Add details to create a data story",
  primaryActionLabel: "Continue",
  // The sources popover has to overlay the footer to show its whole list — see
  // `bodyOverflowY` on the step contract. Safe here: goal + audience + sources
  // cannot outgrow the 90vh shell, so nothing needs the scroll fallback.
  bodyOverflowY: "visible",
  Body: AiStoryDetailsStep,
  initialValues: emptyAiStoryDetails,
  // The frame marks nothing required and the AI can plan from an empty form
  // (that is the whole point of the method), so Continue is always live. Goal
  // and sources only narrow what it plans.
  canContinue: () => true,
  nextStepId: () => null,
  closePopovers: ({ isAddingSource, isModelOpen }) =>
    isAddingSource || isModelOpen
      ? { isAddingSource: false, isModelOpen: false }
      : null,
});

/** Figma 5326:88960 — goal and sources for a story an agent plans. */
const agentDetailsStep = defineDataStoryStep<AgentStoryDetailsValues>({
  id: "agent-details",
  // The one frame in the flow whose header is not fixed copy: it names the
  // agent card the user clicked. Figma 5326:88962 uses an en dash.
  title: ({ recipeId }) => `Agent \u2013 ${agentRecipeTitle(recipeId)}`,
  subtitle: "Add details to create a data story",
  primaryActionLabel: "Continue",
  // Same as the AI frame, and with one field fewer there is even less chance of
  // the body outgrowing the shell.
  bodyOverflowY: "visible",
  Body: AgentStoryDetailsStep,
  initialValues: emptyAgentStoryDetails,
  // Nothing is marked required in the frame, and the agent can plan from its
  // own definition alone — goal and sources only narrow what it plans.
  canContinue: () => true,
  nextStepId: () => null,
  closePopovers: ({ isAddingSource, isModelOpen }) =>
    isAddingSource || isModelOpen
      ? { isAddingSource: false, isModelOpen: false }
      : null,
});

/**
 * The goal step for a tenant on the new Story path. No longer reached: the `method` step asks the
 * goal itself. Kept so the step ids and their tests stay valid until the old path is removed.
 *
 * Originally reached from `method` when the user starts with AI. A Story there is made from its first message alone:
 * the goal is that message, so it is required, and the body draws no sources
 * (`sourcesFromModel`). No templates or agents, which that path doesn't take.
 */
const modelDetailsStep = defineDataStoryStep<AiStoryDetailsValues>({
  id: "model-details",
  title: "Create data story",
  subtitle: "Say what the story should answer",
  primaryActionLabel: "Continue",
  Body: AiStoryDetailsStep,
  initialValues: emptyAiStoryDetails,
  canContinue: ({ goal }) => goal.trim().length > 0,
  nextStepId: () => null,
});

export const DATA_STORY_STEP_REGISTRY: DataStoryStepRegistry = {
  recipe: recipeStep,
  method: methodStep,
  "blank-details": blankDetailsStep,
  "ai-details": aiDetailsStep,
  "agent-details": agentDetailsStep,
  "model-details": modelDetailsStep,
};

/** The step the flow opens on. */
export const FIRST_STEP_ID: DataStoryStepId = "recipe";

/** The step the flow opens on for a tenant on the new Story path. */
export const NEW_PATH_FIRST_STEP_ID: DataStoryStepId = "method";

export const getDataStoryStep = (id: DataStoryStepId) =>
  DATA_STORY_STEP_REGISTRY[id];

/**
 * The step's header title, whether it is fixed copy or derived from what the
 * user picked. Here rather than in the organism so the organism stays a
 * compositor with no conditional of its own.
 */
export const resolveStepTitle = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: DataStoryStepConfig<any>,
  values: unknown,
): string => (typeof step.title === "function" ? step.title(values) : step.title);

/** The footer primary button's label, fixed or derived from the step's values. */
export const resolveStepPrimaryActionLabel = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: DataStoryStepConfig<any>,
  values: unknown,
): string =>
  typeof step.primaryActionLabel === "function"
    ? step.primaryActionLabel(values)
    : step.primaryActionLabel;
